import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Users } from "lucide-react";
import { formatDateRange, formatPrice, categoryLabel, stageBasePath } from "@/lib/format";

const StageCard = ({ stage, muted = false }) => {
  const status = stage.effective_status || stage.status;
  const isOpen = status === "open";
  const detailPath = `${stageBasePath(stage)}/${stage.slug}`;
  const duration = stage.duration_days ? `${stage.duration_days} jour${stage.duration_days > 1 ? "s" : ""}` : "";

  return (
    <article
      data-testid={`stage-card-${stage.slug}`}
      className={`group bg-noir-surface border border-noir-border hover:border-gold transition-colors duration-300 flex flex-col ${muted ? "opacity-60" : ""}`}
    >
      <Link to={detailPath} data-testid={`stage-card-link-${stage.slug}`} className="relative block h-60 overflow-hidden">
        <img
          src={stage.image}
          alt={stage.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-transparent" />
        {isOpen ? (
          <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-[0.15em] bg-royal-light text-white px-3 py-1">
            {stage.places_restantes} place{stage.places_restantes > 1 ? "s" : ""} restante{stage.places_restantes > 1 ? "s" : ""}
          </span>
        ) : status === "full" ? (
          <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-[0.15em] bg-rouge text-white px-3 py-1">
            Complet
          </span>
        ) : (
          <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-[0.15em] bg-white/15 text-white/70 px-3 py-1">
            Inscriptions fermées
          </span>
        )}
        <p className="absolute bottom-4 left-4 right-4 font-display text-3xl tracking-wide text-white uppercase">
          {stage.name}
        </p>
      </Link>

      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-center gap-3 text-gold font-display text-2xl tracking-wider">
          <CalendarDays size={18} className="shrink-0" />
          {formatDateRange(stage.start_date, stage.end_date)}
          {duration && (
            <span className="text-white/40 text-sm font-body font-semibold normal-case tracking-normal">
              — {duration}
            </span>
          )}
        </div>
        <p className="mt-3 flex items-center gap-2 text-white/60 text-sm">
          <MapPin size={14} className="text-gold/70 shrink-0" />
          {stage.location}
          {stage.location_details ? ` — ${stage.location_details}` : ""}
        </p>

        <div className="mt-4 flex items-center gap-2">
          <Users size={14} className="text-gold/70 shrink-0" />
          <span className="text-[12px] font-bold tracking-[0.15em] text-white/75 uppercase">
            {categoryLabel(stage.categories)}
          </span>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10 flex items-end justify-between gap-4 flex-1">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">À partir de</p>
            <p className="font-display text-4xl text-gold leading-none mt-1">{formatPrice(stage.price)}</p>
          </div>
          {isOpen ? (
            <Link
              to={`/inscription/${stage.slug}`}
              data-testid={`stage-cta-${stage.slug}`}
              className="px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] bg-rouge text-white rounded-full hover:bg-rouge-hover hover:scale-[1.03] active:scale-95 transition-all shadow-[0_0_18px_rgba(230,0,0,0.25)]"
            >
              S'inscrire
            </Link>
          ) : (
            <span
              data-testid={`stage-cta-${stage.slug}`}
              className="px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] bg-white/10 text-white/40 cursor-not-allowed rounded-full"
            >
              {status === "full" ? "Complet" : "Inscriptions fermées"}
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default StageCard;
